import { Component, Inject, TemplateRef, ViewChild , OnDestroy} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IAppConfig } from './core/config/app-config.model';
import { APP_CONFIG } from './core/config/app-config.service';
import { PlatformService } from './core/platform.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { AppService } from './core/app.service';
import { AuthService } from './core/api/auth.service';
import { KeycloakEventTypeLegacy, KeycloakService } from 'keycloak-angular';
import { Subject, takeUntil } from 'rxjs';
import { LogService } from './core/log.service';
import { XnStorageService } from './core/xn-storage';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
})
export class AppComponent implements OnDestroy {
  @ViewChild('timingOutDialog') timingOutDialog: TemplateRef<any>;

  public isLoggedIn = false;
  public userProfile: any = null;
  public token: string = '';

  sidebar = false;
  isAuthenticated = false;

  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  isSetTimeOut: boolean = true;
  destroyed = new Subject();
  isShowTimeoutDialog = false;

  constructor(
    private storageService: XnStorageService,
    public translateService: TranslateService,
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private platform: PlatformService,
    private router: Router,
    private idle: Idle,
    private keepalive: Keepalive,
    private appService: AppService,
    private authService: AuthService,
    private readonly keycloak: KeycloakService,
    private log: LogService,
  ) {}

  public async ngOnInit() {
    let isUsingExternalIdentityServer = this.config.isUsingExternalIdentityServer;
    if(this.config.themeDefault){
      document.querySelector('body').classList.add(this.config.themeDefault.toLowerCase());
    }

    this.isAuthenticated = this.authService.isLoggedIn();
    this.authService.onAuthChanged().pipe(takeUntil(this.destroyed)).subscribe(id => {
      this.isAuthenticated = id ? true: false;
    });

    this.authService.monitor();
    
    this.keycloak.keycloakEvents$.subscribe({
      next: (e) => {
        if(e.type == KeycloakEventTypeLegacy.OnTokenExpired){
          this.keycloak.updateToken(1200).then(res => {
            
          })
        }
      }
    })

    this.appService.getIsLogout().subscribe((res) => {
      if (res) {
        if(isUsingExternalIdentityServer){
          this.logout();
        }
        else{
          // redirect to login page
          this.router.navigateByUrl(this.platform.url(''));
        }
      }
    });

    this.appService.getSetTimeOut().subscribe((t) => {
      this.isSetTimeOut = t;
    });

    this.platform.setIsLoggedIn(this.authService.isLoggedIn());
    this.authService.getProfileId().subscribe((id) => {
      this.platform.setIsLoggedIn(id ? true : false);
    });

    if (this.isSetTimeOut) {
      this.initTimeoutProcess();
    }

    let redirectTo = null;
    const searchParams = new URLSearchParams(window.location.search);
    if(searchParams.has('redirect_to')){
      redirectTo = searchParams.get('redirect_to');
    }

    if(!this.authService.isLoggedIn() && !/(activate-application|redirect-login)/i.test(window.location.href)){
      this.log.log('App component not logged in captureIDPAuth');
      this.authService.captureIDPAuth({
        redirectTo: redirectTo
      });;
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next(null);
  }

  onTimeoutDialogHide(){
  }

  private initTimeoutProcess() {
    const permitIdleTimeoutInSeconds = Math.max(this.config.timeout * 60, 60); // min: 60 seconds, max: per config
    this.idle.setIdle(permitIdleTimeoutInSeconds);

    // sets a timing-out period of 20 seconds. after 20 seconds of inactivity, the user will be considered timed out.
    this.idle.setTimeout(20);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleEnd.subscribe(() => {
      this.tryCloseTimingOutDialog();
      this.idleState = 'No longer idle.'
      this.resetTimeout();
    });

    this.idle.onTimeout.subscribe(() => {
      this.tryCloseTimingOutDialog();
      this.idleState = 'Timed out!';
      this.timedOut = true;

      this.authService.sessionTimeout(); 
    });

    this.idle.onIdleStart.subscribe(() => {
      this.idleState = 'You\'ve gone idle!'

      this.isShowTimeoutDialog = true;
    });

    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = this.translateService.instant('common.message.timing-out', { seconds: countdown });
    });

    // sets the ping interval to 15 seconds
    this.keepalive.interval(15);
    this.keepalive.onPing.subscribe(() => {
      this.lastPing = new Date()
    });

    this.authService.getProfileId().subscribe((id) => {
      if (id) {
        this.idle.watch();
        this.timedOut = false;
      } else {
        this.idle.stop();
      }
    });

    if(this.authService.isLoggedIn()){
      this.idle.watch();
    }
  }

  resetTimeout() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
    this.tryCloseTimingOutDialog();
  }

  extendTimeout() {
    this.resetTimeout();

    this.authService.autoRefreshToken();
  }

  tryCloseTimingOutDialog() {
    this.isShowTimeoutDialog = false;
  }

  isLoginPage(pageUrl: string): boolean {
    return /\/login*/.test(pageUrl);
  }

  reloadPage() {
    window.location.reload();
  }
 
  logout() {
    this.authService.logout();
  }

}
